import type { UserWithPermissionsInDto } from 'shared/domain/user/types/inDto';
import { debugLog } from 'shared/logger/debugLog';
import { isBelowSuperAdminPermission, UserRole } from 'shared/types/userRole';

/*
 * The user's role in a project, read from the DTO: shared by the model and
 * view mappings, so neither of them loads the other for it.
 */

function extractRoleFromBasePermissions(
  basePermissions: UserWithPermissionsInDto['basePermissions'],
  organizationId: string,
  projectId: string
): UserRole | undefined {
  // SuperAdmin: top-level role (access to everything)
  if ('role' in basePermissions && basePermissions.role) {
    return basePermissions.role;
  }

  if (!isBelowSuperAdminPermission(basePermissions)) {
    return undefined;
  }

  // Find the client matching the organization
  const client = basePermissions.clients.find((c) => c._id === organizationId);
  if (!client) {
    return undefined;
  }

  // OrganizationAdmin for this specific organization
  if ('role' in client && client.role) {
    return client.role;
  }

  // Project-level role from this organization's projects
  if ('projects' in client && client.projects) {
    const projectPermission = client.projects.find((p) => p._id === projectId);
    if (projectPermission?.role) {
      return projectPermission.role;
    }
  }

  return undefined;
}

export function getRoleFromUserWithPermissionsInDto(
  userWithPermissionsInDto: UserWithPermissionsInDto,
  organizationId: string,
  currentProjectId: string
): UserRole {
  const roleFromBase = extractRoleFromBasePermissions(
    userWithPermissionsInDto.basePermissions,
    organizationId,
    currentProjectId
  );
  if (roleFromBase) {
    return roleFromBase;
  }

  // Fallback: check flat permissions array
  const projectRole = userWithPermissionsInDto.permissions.find(
    (permission) => permission.project === currentProjectId
  )?.role;
  if (projectRole) {
    return projectRole;
  }

  debugLog('returning default role');
  return UserRole.standard;
}
